import axios, { AxiosInstance, AxiosResponse } from "axios";
import { captureApiError } from "@/observability/sentry";
import { toastService } from "@/services/toastService";
import { isTokenExpired, isTokenExpiringSoon, shouldEndSessionOnError } from "@/utils/auth";
import type { AuthResponse, ApiResponse } from "@/types";

export function buildQueryString(
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (typeof value === "boolean") {
      queryParams.append(key, String(value));
      return;
    }

    queryParams.append(key, String(value));
  });

  const stringified = queryParams.toString();
  return stringified ? `?${stringified}` : "";
}

export function createHttpClient() {
  let isRefreshing = false;
  let refreshSubscribers: ((token: string | null) => void)[] = [];

  const baseURL = import.meta.env.VITE_API_BASE_URL || "/api/v1";

  // Bounded wait so requests fail on flaky networks instead of hanging
  // forever. Long payloads (telemetry file uploads) override this per request.
  const DEFAULT_TIMEOUT_MS = 30_000;

  function isValidToken(token: string | null): token is string {
    return token !== null && token !== "null" && token !== "";
  }

  function isAuthEndpoint(url: string | undefined): boolean {
    if (!url) return false;
    return /^\/?auth\/(?:login|logout|register|refresh|change-password|invitations\/accept)\/?(?:\?.*)?$/.test(
      url,
    );
  }

  function handleLogout(): void {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    toastService.sessionExpired();
    // Full page load, not a SPA navigation: after credential loss React
    // Router's in-memory state is stale, and synthesizing a popstate to
    // redirect can crash mid-recovery ("Router inside another Router"
    // invariant, Sentry RACE-MANAGER-FRONTEND-4).
    window.location.assign("/login");
  }

  const client: AxiosInstance = axios.create({
    baseURL,
    timeout: DEFAULT_TIMEOUT_MS,
    headers: {
      "Content-Type": "application/json",
    },
    transformRequest: [],
  });

  async function refreshToken(): Promise<AuthResponse> {
    const token = localStorage.getItem("refreshToken");
    if (!isValidToken(token)) {
      throw new Error("No refresh token found");
    }

    const response: AxiosResponse<ApiResponse<AuthResponse>> =
      await client.post("/auth/refresh", { refreshToken: token });
    return response.data.data;
  }

  async function handleTokenRefresh(): Promise<void> {
    if (isRefreshing) {
      return new Promise<void>((resolve, reject) => {
        refreshSubscribers.push((token) =>
          token ? resolve() : reject(new Error("Refresh failed")),
        );
      });
    }

    // A missing refresh token is unrecoverable even though it produces no
    // HTTP error to classify below.
    const hasRefreshToken = isValidToken(localStorage.getItem("refreshToken"));
    isRefreshing = true;

    try {
      const { user, token, refreshToken: rotatedToken } = await refreshToken();

      localStorage.setItem("token", token);
      if (rotatedToken) {
        localStorage.setItem("refreshToken", rotatedToken);
      }
      localStorage.setItem("user", JSON.stringify(user));

      refreshSubscribers.forEach((callback) => callback(token));
      refreshSubscribers = [];
    } catch (error) {
      // Connectivity/server outages must not destroy the session; only a
      // missing refresh token, an explicit server rejection, or a real
      // timeout ends it. Queued requests still fail so callers can retry.
      if (!hasRefreshToken || shouldEndSessionOnError(error)) {
        handleLogout();
      }
      refreshSubscribers.forEach((callback) => callback(null));
      refreshSubscribers = [];
      throw error;
    } finally {
      isRefreshing = false;
    }
  }

  client.interceptors.request.use(
    async (config) => {
      const isAuthReq = isAuthEndpoint(config.url);

      if (
        config.data &&
        typeof config.data === "object" &&
        !(config.data instanceof FormData)
      ) {
        config.data = JSON.stringify(config.data);
        if (config.headers) {
          config.headers["Content-Type"] = "application/json";
        }
      }

      let token = localStorage.getItem("token");
      if (isValidToken(token) && !isAuthReq) {
        if (isTokenExpired(token)) {
          try {
            await handleTokenRefresh();
            const newToken = localStorage.getItem("token");
            if (isValidToken(newToken)) {
              token = newToken;
            }
          } catch (error) {
            // handleTokenRefresh already logged the user out; keep the rejection.
            return Promise.reject(error);
          }
        } else if (isTokenExpiringSoon(token)) {
          handleTokenRefresh().catch(() => {});
        }

        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Report 5xx/network failures to Sentry (no-op when uninitialized).
      captureApiError(error);
      const originalRequest = error.config;
      const storedToken = localStorage.getItem("token");

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        isValidToken(storedToken) &&
        !isAuthEndpoint(originalRequest.url)
      ) {
        originalRequest._retry = true;

        try {
          await handleTokenRefresh();
          const newToken = localStorage.getItem("token");
          if (isValidToken(newToken)) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return client(originalRequest);
          }
        } catch (refreshError) {
          // handleTokenRefresh already logged the user out; keep the rejection.
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );

  const onlineRetryController = new AbortController();
  window.addEventListener(
    "online",
    () => {
      const token = localStorage.getItem("token");
      if (!isValidToken(token)) return;
      if (!isTokenExpired(token) && !isTokenExpiringSoon(token)) return;
      handleTokenRefresh().catch(() => {});
    },
    { signal: onlineRetryController.signal }
  );

  return { client, refreshToken, dispose: () => onlineRetryController.abort() };
}

export type HttpClient = ReturnType<typeof createHttpClient>;
